/**
 * TemplateBrowserModal.js
 *
 * Browse and add pre-built operation templates from DEFAULT_OPERATION_TEMPLATES.
 * Phase 3 of the Custom Operation Types feature.
 */

import React, { useState, useMemo } from 'react';
import { X, Plus, Check, ChevronRight, ChevronDown, FileText, CheckCircle } from 'lucide-react';
import { DEFAULT_OPERATION_TEMPLATES, OPERATION_TYPE_LABELS } from '../../../../config/operationsConfig';

const TemplateBrowserModal = ({
  isOpen,
  onClose,
  existingOperations = [],
  onAddTemplate
}) => {
  const [expandedTemplate, setExpandedTemplate] = useState(null);

  // Build list of available templates from DEFAULT_OPERATION_TEMPLATES
  // Note: DEFAULT_OPERATION_TEMPLATES is an ARRAY of objects with {id, label, fields, checks}
  const templates = useMemo(() => {
    return (DEFAULT_OPERATION_TEMPLATES || []).map((template) => {
      const isAdded = existingOperations.some(op => op.id === template.id);

      // Extract fields and checks from the template
      const fields = template.fields || [];
      const checks = template.phases
        ? template.phases.flatMap(p => p.checks || [])
        : (template.checks || []);

      return {
        id: template.id,
        label: template.label || OPERATION_TYPE_LABELS[template.id] || template.id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        fields,
        checks,
        isMultiPhase: !!template.phases,
        phasesCount: template.phases?.length || 0,
        isAdded
      };
    });
  }, [existingOperations]);

  if (!isOpen) return null;

  const handleAddTemplate = (template) => {
    if (template.isAdded) return;

    // Build the operation object to add
    const operationToAdd = {
      id: template.id,
      label: template.label,
      fields: template.fields,
      checks: template.checks
    };

    onAddTemplate(operationToAdd);
  };

  return (
    <>
      {/* Backdrop - z-[60] to appear above VoyageSettingsModal */}
      <div
        className="fixed inset-0 bg-black/40 z-[60]"
        onClick={onClose}
      />

      {/* Slide-up Panel - z-[60] to appear above VoyageSettingsModal */}
      <div className="fixed bottom-0 left-0 right-0 z-[60] animate-slide-up">
        <div className="bg-white rounded-t-2xl shadow-xl border-t border-slate-200 max-w-lg mx-auto safe-area-bottom max-h-[85vh] flex flex-col">
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 bg-slate-300 rounded-full"></div>
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-5 pb-3 border-b border-slate-100">
            <div>
              <h3 className="font-semibold text-slate-900">Browse Templates</h3>
              <p className="text-xs text-slate-500">Add pre-built operation templates</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Templates List */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {templates.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">
                No templates available
              </p>
            ) : (
              <div className="space-y-2">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    className={`border rounded-lg overflow-hidden ${
                      template.isAdded
                        ? 'border-green-200 bg-green-50'
                        : 'border-slate-200 bg-white'
                    }`}
                  >
                    {/* Template Header */}
                    <div
                      className="flex items-center gap-3 px-4 py-3 cursor-pointer"
                      onClick={() => setExpandedTemplate(
                        expandedTemplate === template.id ? null : template.id
                      )}
                    >
                      <div className={`p-2 rounded-lg ${
                        template.isAdded
                          ? 'bg-green-100'
                          : 'bg-cyan-50'
                      }`}>
                        <FileText className={`h-4 w-4 ${
                          template.isAdded
                            ? 'text-green-600'
                            : 'text-cyan-600'
                        }`} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-slate-900 truncate">
                          {template.label}
                        </h4>
                        <p className="text-xs text-slate-500">
                          {template.fields.length} field{template.fields.length !== 1 ? 's' : ''} • {template.checks.length} check{template.checks.length !== 1 ? 's' : ''}
                          {template.isMultiPhase && ` • ${template.phasesCount} phases`}
                        </p>
                      </div>

                      {template.isAdded ? (
                        <span className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                          <Check className="h-3 w-3" /> Added
                        </span>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddTemplate(template);
                          }}
                          className="flex items-center gap-1 text-xs text-white bg-cyan-600 hover:bg-cyan-700 px-2 py-1 rounded"
                        >
                          <Plus className="h-3 w-3" /> Add
                        </button>
                      )}

                      {expandedTemplate === template.id ? (
                        <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      )}
                    </div>

                    {/* Expanded Details */}
                    {expandedTemplate === template.id && (
                      <div className="px-4 pb-4 border-t border-slate-100">
                        {/* Fields */}
                        {template.fields.length > 0 && (
                          <div className="mt-3">
                            <h5 className="text-xs font-medium text-slate-600 mb-2">Data Fields</h5>
                            <div className="space-y-1">
                              {template.fields.map((field, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-xs">
                                  <span className="w-2 h-2 bg-cyan-400 rounded-full"></span>
                                  <span className="text-slate-700">{field.label}</span>
                                  <span className="text-slate-400">({field.type})</span>
                                  {field.required && (
                                    <span className="text-red-500">*</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Checks */}
                        {template.checks.length > 0 && (
                          <div className="mt-3">
                            <h5 className="text-xs font-medium text-slate-600 mb-2">Pre-Op Checks</h5>
                            <div className="space-y-1">
                              {template.checks.slice(0, 5).map((check, idx) => (
                                <div key={idx} className="flex items-start gap-2 text-xs">
                                  <CheckCircle className="h-3 w-3 text-slate-400 flex-shrink-0 mt-0.5" />
                                  <span className="text-slate-700">
                                    {typeof check === 'string' ? check : check.text}
                                  </span>
                                </div>
                              ))}
                              {template.checks.length > 5 && (
                                <p className="text-xs text-slate-400 pl-5">
                                  +{template.checks.length - 5} more checks
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {template.fields.length === 0 && template.checks.length === 0 && (
                          <p className="mt-3 text-xs text-slate-400 italic">
                            No fields or checks configured
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-4 border-t border-slate-100 bg-slate-50">
            <button
              onClick={onClose}
              className="w-full py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TemplateBrowserModal;
